import { Paperclip, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ResourceCertification } from '@my/directory-api';
import { formatFileSize } from './lib/resourceCertHelpers';

interface AttachmentCellProps {
  cert: ResourceCertification;
  isBusy: boolean;
  onUpload: (file: File) => void | Promise<void>;
  onRemove: () => void | Promise<void>;
}

/**
 * Drag-drop / click attachment cell for a single certification row.
 *
 * Three visual states:
 *   1. Empty — dashed-border drop zone, "Drop / click" hint.
 *   2. Filled — solid border with paperclip + filename + size + version
 *      badge (`v3`). Clicking the filename downloads via the SAS URL;
 *      the × removes the attachment metadata (BE leaves the blob in
 *      storage as audit history).
 *   3. Dragging — blue tint while a file is being held over the cell.
 *
 * Uploads use `cert.attachmentUrl` for the download link — that's a
 * short-lived SAS URL minted BE-side; we treat it as ephemeral.
 */
export function AttachmentCell({ cert, isBusy, onUpload, onRemove }: AttachmentCellProps) {
  const { t } = useTranslation('resources');
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  // The BE clears all attachment columns together when an attachment is
  // removed, so any one of them being non-null is enough — picking
  // attachmentFileName because it's the user-facing signal ("there's a
  // file called X here") and survives the FE not having the SAS URL
  // for some reason.
  const hasAttachment = cert.attachmentFileName != null;

  const handleFiles = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file || isBusy) return;
    await onUpload(file);
  };

  if (hasAttachment) {
    return (
      <div className="flex items-center gap-1.5 rounded border border-slate-200 bg-white px-1.5 py-1 text-[10px]">
        <Paperclip className="h-3 w-3 flex-shrink-0 text-slate-400" aria-hidden="true" />
        {cert.attachmentUrl ? (
          <a
            href={cert.attachmentUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="max-w-[100px] truncate text-slate-700 hover:text-blue-600 hover:underline"
            title={`${cert.attachmentFileName ?? ''} ${formatFileSize(cert.attachmentFileSizeBytes)}`}
          >
            {cert.attachmentFileName ?? '—'}
          </a>
        ) : (
          <span className="max-w-[100px] truncate text-slate-700">
            {cert.attachmentFileName ?? '—'}
          </span>
        )}
        {cert.attachmentVersion > 0 && (
          <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-medium text-slate-600">
            v{cert.attachmentVersion}
          </span>
        )}
        <button
          type="button"
          onClick={() => onRemove()}
          disabled={isBusy}
          aria-label={t('attachment.remove')}
          className="ml-auto flex-shrink-0 text-slate-300 transition-colors hover:text-red-400 disabled:opacity-50"
        >
          <X className="h-2.5 w-2.5" />
        </button>
      </div>
    );
  }

  return (
    <div
      onClick={() => !isBusy && inputRef.current?.click()}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragging(false);
        void handleFiles(e.dataTransfer.files);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        setIsDragging(false);
      }}
      className={`flex cursor-pointer items-center gap-1 rounded border px-1.5 py-1 text-[10px] transition-colors ${
        isDragging
          ? 'cursor-copy border-blue-400 bg-blue-50'
          : 'border-dashed border-slate-300 bg-white hover:border-slate-400 hover:bg-slate-50'
      } ${isBusy ? 'cursor-default opacity-50' : ''}`}
    >
      <Paperclip className="h-3 w-3 flex-shrink-0 text-slate-400" aria-hidden="true" />
      <span className="text-slate-400">{t('attachment.dropOrClick')}</span>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={(e) => {
          void handleFiles(e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );
}
